import React from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import TextField from '../../../components/Inputs/TextField';
import Box from '../../../components/Styled/Box';
import Button from '../../../components/Buttons/Button';
import Checkbox from '../../../components/Inputs/Checkbox';
import Typography from '../../../components/Typography/Typography';

import { applicationActions, authActions } from '../../../redux/actions';

import { useIsMobileLayout, useCustomLayout, useRoutes } from '../../../hooks';
import PasswordField from '../../../components/Inputs/PasswordField';
import { RegisterImg } from '../../../assets/images';

const validationSchema = (f) => Yup.object().shape({
  email: Yup.string()
    .email(f({ id: 'errors.invalid.email' }))
    .required(f({ id: 'errors.required.email' })),
  password: Yup.string().required(f({ id: 'errors.required.password' })),
});

const LoginForm = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useIsMobileLayout();
  const { formatMessage: f } = useIntl();
  const routes = useRoutes();

  useCustomLayout({
    image: RegisterImg,
    color: true,
  });

  const toggleWarningDialog = () => dispatch(applicationActions.toggleDialog({
    title: f({ id: 'forms.login.warning_dialog.title' }),
    body: f({ id: 'forms.login.warning_dialog.message' }),
    leftButton: {
      text: f({ id: 'forms.login.warning_dialog.cancel_button' }),
      props: {
        link: routes.unauthenticatedContact,
      },
    },
    button: {
      text: f({ id: 'forms.login.warning_dialog.confirm_button' }),
    },
  }));

  const onSubmit = (values) => dispatch(
    authActions.authenticateRequest({
      email: values.email,
      password: values.password,
      stayLoggedIn: values.stayLoggedIn,
    }),
  );

  const formik = useFormik({
    initialValues: { email: '', password: '', stayLoggedIn: true },
    onSubmit,
    validateOnMount: true,
    validationSchema: validationSchema(f),
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <TextField
        boxProps={{ mt: 3 }}
        name='email'
        type='email'
        value={formik.values.email}
        onChange={formik.handleChange('email')}
        onBlur={formik.handleBlur('email')}
        label={f({ id: 'forms.login.email_label' })}
        placeholder={f({ id: 'forms.login.email_placeholder' })}
        error={formik.touched.email ? formik.errors.email : ''}
      />

      <PasswordField
        boxProps={{ mt: 3 }}
        name='password'
        type='password'
        value={formik.values.password}
        onChange={formik.handleChange('password')}
        onBlur={formik.handleBlur('password')}
        label={f({ id: 'forms.login.password_label' })}
        placeholder='******'
        error={formik.touched.password ? formik.errors.password : ''}
        helperText={(
          <Link to={routes.recoverPassword} className='underline'>
            {f({ id: 'forms.login.forgot_password' })}
          </Link>
        )}
      />

      <Box mt={3}>
        <Checkbox
          label={f({ id: 'pages.login.stay_logged_in' })}
          value={formik.values.stayLoggedIn}
          checked={formik.values.stayLoggedIn}
          onChange={(value) => formik.setFieldValue('stayLoggedIn', !value)}
        />
      </Box>

      <Box
        mt='40px'
        display='flex'
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent={isMobile ? 'flex-start' : 'flex-end'}
      >
        <Button
          width={isMobile ? '75%' : ''}
          centerText={isMobile}
          type='button'
          variant='secondary'
          link={`${routes.signup}${location.search}`}
        >
          {f({ id: 'forms.login.signup_button' })}
        </Button>

        <Button
          width={isMobile ? '75%' : ''}
          centerText={isMobile}
          {...isMobile ? { mt: 2 } : { ml: 2 }}
          type='submit'
          disabled={!formik.isValid && !formik.isSubmitting}
        >
          {f({ id: 'forms.login.login_button' })}
        </Button>
      </Box>
    </form>
  );
};

export default LoginForm;
